import { ArrowLeftCircleIcon, ArrowRightCircleIcon, CalendarDaysIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { getTridImportantDates } from 'services'

import { floaterPanelEdgeClass, floaterTabEdgeClass, FloaterTabWrapper, useFloaterTab } from './useFloaterTab'

interface TridRuleStatus {
  applies: boolean
  status: 'not_applicable' | 'waiting' | 'due' | 'satisfied' | 'ineligible'
  dueDate: string
  disclosedDate: string
  message?: string
}

const LoanEstimateDueDateContent = ({ status }: { status: TridRuleStatus }) => {
  const [isOpen, setOpen] = useState(false)
  const floater = useFloaterTab('loanEstimateDueDate', {
    side: 'left',
    percent: 50,
    mobileTop: 472,
  })
  const isIneligible = status.status === 'ineligible'
  const colorClasses = isIneligible
    ? 'ring-red-600 bg-red-600 border-red-600 hover:text-red-600'
    : 'ring-shade-blue bg-shade-blue border-shade-blue hover:text-shade-blue'

  return (
    <>
      <FloaterTabWrapper floater={floater}>
        <button
          className={`p-3 text-sm ${floaterTabEdgeClass(
            floater.side,
          )} ring-[1px] ${colorClasses} text-white border hover:bg-white rounded flex items-center gap-2 w-auto sm:w-[210px]`}
          {...floater.tabProps(() => setOpen(!isOpen))}
        >
          <CalendarDaysIcon className="w-6 h-6 shrink-0" />
          <span className="hidden sm:flex flex-col text-left leading-tight">
            <span>Loan Estimate Due Date</span>
            <span className="font-bold">{status.dueDate}</span>
          </span>
        </button>
      </FloaterTabWrapper>

      {isOpen && (
        <div
          className={`z-50 shadow1 fixed bg-white border top-[5vh] bottom-0 w-[26rem] max-w-full h-[90vh] overflow-auto ease-in-out ${floaterPanelEdgeClass(
            floater.side,
          )}`}
        >
          <div className="w-[26rem] max-w-full fixed z-20 rounded-t flex justify-between p-3 font-bold text-sm items-center border-b bg-white">
            <p className={isIneligible ? 'text-red-700 text-lg' : 'text-gray-800 text-lg'}>Loan Estimate Due Date</p>
            <button className="hover:underline hover-shadow1 p-1 rounded" onClick={() => setOpen(false)}>
              {floater.side === 'left' ? (
                <ArrowLeftCircleIcon className="w-5 h-5" />
              ) : (
                <ArrowRightCircleIcon className="w-5 h-5" />
              )}
            </button>
          </div>

          <div className="pt-20 px-5 text-sm text-gray-900">
            <p className="text-gray-600">Loan Estimate must be delivered or placed in the mail by:</p>
            <p className="mt-2 text-2xl font-bold">{status.dueDate}</p>

            {isIneligible && (
              <div className="mt-6 rounded border border-red-300 bg-red-50 p-4 text-red-800">
                <p className="font-semibold">Disclosed Date: {status.disclosedDate}</p>
                <p className="mt-2 font-bold">{status.message}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export const LoanEstimateDueDateFloater = ({ loanNumber }: { loanNumber: string }) => {
  const [status, setStatus] = useState<TridRuleStatus | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = () => {
      getTridImportantDates()
        .then((response: any) => {
          if (!cancelled) setStatus(response?.tridRuleStatus || null)
        })
        .catch(() => {
          if (!cancelled) setStatus(null)
        })
    }

    load()
    window.addEventListener('trid-important-dates-updated', load)
    return () => {
      cancelled = true
      window.removeEventListener('trid-important-dates-updated', load)
    }
  }, [loanNumber])

  if (!status || (status.status !== 'due' && status.status !== 'ineligible')) return null
  return <LoanEstimateDueDateContent status={status} />
}
